<template>
    <div class="workflow-config">
        <div class="workflow-overview">
            <div class="workflow-overview__item">
                <span class="workflow-overview__label">Действия</span>
                <strong>{{ enabledActions.length }} / {{ allActions.length }}</strong>
            </div>
            <div class="workflow-overview__item">
                <span class="workflow-overview__label">Переходы</span>
                <strong>{{ transitionCount }}</strong>
            </div>
            <div class="workflow-overview__item">
                <span class="workflow-overview__label">Автопереходы</span>
                <strong>{{ autoTransitionCount }}</strong>
            </div>
            <div class="workflow-overview__item">
                <span class="workflow-overview__label">Проверки</span>
                <strong>{{ workflowProblems.length ? workflowProblems.length : 'OK' }}</strong>
            </div>
        </div>

        <b-alert
            v-if="workflowProblems.length"
            show
            variant="warning"
            class="mb-3"
        >
            <div class="font-weight-bold mb-2">
                <i class="fas fa-exclamation-triangle"></i>
                Что нужно проверить
            </div>
            <div
                v-for="problem in workflowProblems"
                :key="problem"
                class="small"
            >
                {{ problem }}
            </div>
        </b-alert>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-route"></i>
                        Карта процесса
                    </h6>
                    <div class="workflow-section__subtitle">
                        Базовые правила маршрута, которые влияют на согласование и приемку.
                    </div>
                </div>
            </div>

            <div class="workflow-process-options">
                <b-form-checkbox
                    v-model="localConfig.sequentialApproval"
                    @change="emitUpdate"
                    switch
                >
                    <strong>Последовательное согласование</strong>
                    <small class="text-muted d-block">Согласующие работают по очереди, а не параллельно.</small>
                </b-form-checkbox>

                <b-form-checkbox
                    v-model="localConfig.requiresAcceptance"
                    @change="emitUpdate"
                    switch
                >
                    <strong>Требуется приемка инициатором</strong>
                    <small class="text-muted d-block">После выполнения заявка перейдет в «Ожидает приемки».</small>
                </b-form-checkbox>

                <b-form-checkbox
                    v-model="localConfig.closingComment.required"
                    @change="emitUpdate"
                    switch
                >
                    <strong>Обязательный комментарий при итоговых действиях</strong>
                    <small class="text-muted d-block">
                        Включите настройку и выберите действия, для которых пояснение должно быть обязательным.
                    </small>
                </b-form-checkbox>

                <div
                    v-if="localConfig.closingComment.required"
                    class="border rounded bg-light p-3 ml-sm-4"
                >
                    <div class="font-weight-bold mb-2">Требовать комментарий при действиях:</div>
                    <b-form-checkbox-group
                        v-model="localConfig.closingComment.requiredActions"
                        :options="closingCommentActionOptions"
                        stacked
                        @change="emitUpdate"
                    />
                    <b-alert show variant="info" class="small mt-3 mb-0">
                        Отклонение, отмена и возврат на доработку всегда требуют пояснение — независимо от выбранных пунктов.
                    </b-alert>
                </div>
            </div>
        </section>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-tasks"></i>
                        Действия, роли и результат
                    </h6>
                    <div class="workflow-section__subtitle">
                        Каждое действие настраивается в одном месте: включение, кто может выполнить и итоговый статус.
                    </div>
                </div>
            </div>

            <div
                v-for="group in actionGroups"
                :key="group.key"
                class="workflow-action-group"
            >
                <div class="workflow-action-group__title">
                    <i :class="group.icon"></i>
                    {{ group.label }}
                </div>

                <div
                    v-for="action in group.actions"
                    :key="action.key"
                    class="workflow-action-row"
                    :class="{ 'workflow-action-row--disabled': !isActionEnabled(action.key) }"
                >
                    <div class="workflow-action-row__main">
                        <b-form-checkbox
                            v-model="localConfig.allowedActions"
                            :value="action.key"
                            :disabled="action.key === 'submit'"
                            @change="emitUpdate"
                            switch
                        >
                            <strong>{{ action.label }}</strong>
                            <small class="text-muted d-block">{{ action.description }}</small>
                        </b-form-checkbox>
                    </div>

                    <div
                        v-if="isActionEnabled(action.key)"
                        class="workflow-action-row__details"
                    >
                        <div class="workflow-role-picker">
                            <div class="workflow-field-label">Кто может выполнить</div>
                            <b-form-checkbox
                                v-for="role in allRoles"
                                :key="`${action.key}-${role.key}`"
                                :checked="hasPermission(action.key, role.key)"
                                class="workflow-role-chip"
                                @change="togglePermission(action.key, role.key, $event)"
                            >
                                {{ role.label }}
                            </b-form-checkbox>
                        </div>

                        <b-form-group
                            class="workflow-result-select"
                            label="Результат действия"
                            :label-for="'action-result-' + action.key"
                        >
                            <v-select
                                :id="'action-result-' + action.key"
                                v-model="localConfig.stateMachineConfig.actionToStatus[action.key]"
                                :options="allStatuses"
                                label="label"
                                :reduce="status => status.key"
                                placeholder="Выберите статус"
                                @input="emitUpdate"
                            >
                                <template #option="option">
                                    <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                                    <small class="text-muted ml-2">{{ option.description }}</small>
                                </template>
                                <template #selected-option="option">
                                    <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                                </template>
                            </v-select>
                        </b-form-group>
                    </div>
                </div>
            </div>
        </section>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-exchange-alt"></i>
                        Разрешенные переходы
                    </h6>
                    <div class="workflow-section__subtitle">
                        Вместо общей матрицы: для каждого исходного статуса выбран список доступных следующих статусов.
                    </div>
                </div>
            </div>

            <div
                v-for="sourceStatus in allStatuses"
                :key="'transition-row-' + sourceStatus.key"
                class="workflow-transition-row"
            >
                <div class="workflow-transition-row__source">
                    <b-badge :variant="sourceStatus.variant">{{ sourceStatus.label }}</b-badge>
                    <small class="text-muted d-block">{{ sourceStatus.description }}</small>
                </div>

                <div class="workflow-transition-row__targets">
                    <v-select
                        v-if="!isFinalStatus(sourceStatus.key)"
                        v-model="localConfig.stateMachineConfig.transitionsMap[sourceStatus.key]"
                        :options="transitionTargetOptions(sourceStatus.key)"
                        label="label"
                        :reduce="status => status.key"
                        multiple
                        placeholder="Выберите доступные переходы"
                        @input="emitUpdate"
                    >
                        <template #option="option">
                            <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                        </template>
                        <template #selected-option="option">
                            <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                        </template>
                    </v-select>
                    <span v-else class="text-muted small">
                        Финальный статус, исходящих переходов нет.
                    </span>
                </div>
            </div>
        </section>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-magic"></i>
                        Автопереходы
                    </h6>
                    <div class="workflow-section__subtitle">
                        Системные переходы после ключевых событий процесса.
                    </div>
                </div>
            </div>

            <b-row>
                <b-col cols="12" md="4">
                    <b-form-group label="После отправки" label-for="auto-submitted">
                        <v-select
                            id="auto-submitted"
                            v-model="localConfig.autoTransitions.submitted"
                            :options="allStatuses"
                            label="label"
                            :reduce="status => status.key"
                            placeholder="Не переходить автоматически"
                            :clearable="true"
                            @input="emitUpdate"
                        >
                            <template #option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                            <template #selected-option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                        </v-select>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="Когда все согласовали" label-for="auto-approved">
                        <v-select
                            id="auto-approved"
                            v-model="localConfig.autoTransitions.allApproved"
                            :options="allStatuses"
                            label="label"
                            :reduce="status => status.key"
                            placeholder="Выберите статус"
                            :clearable="true"
                            @input="emitUpdate"
                        >
                            <template #option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                            <template #selected-option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                        </v-select>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="Когда кто-то отклонил" label-for="auto-rejected">
                        <v-select
                            id="auto-rejected"
                            v-model="localConfig.autoTransitions.anyRejected"
                            :options="allStatuses"
                            label="label"
                            :reduce="status => status.key"
                            placeholder="Выберите статус"
                            :clearable="true"
                            @input="emitUpdate"
                        >
                            <template #option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                            <template #selected-option="option">
                                <b-badge :variant="option.variant">{{ option.label }}</b-badge>
                            </template>
                        </v-select>
                    </b-form-group>
                </b-col>
            </b-row>
        </section>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-users-cog"></i>
                        Управление участниками заявки
                    </h6>
                    <div class="workflow-section__subtitle">
                        Кто может менять согласующих, назначающих, исполнителей, наблюдателей и инициатора в уже созданной заявке.
                    </div>
                </div>
            </div>

            <MultipleParticipantsConfig
                v-model="localConfig.approverManagement.managers"
                :users="users"
                :departments="departments"
                participant-label="управляющего"
                type-label="Тип доступа"
                all-text="Любой пользователь системы сможет управлять участниками заявки"
                empty-text="Управляющие не настроены"
                hint-text="Можно выбрать роль в заявке, конкретных пользователей, отдел или роль на объекте"
                :show-object-roles="true"
                :show-request-roles="true"
                :show-fallback-option="false"
                :show-priority="false"
                :show-manager-permissions="true"
                :managed-role-options="managerManagedRoleOptions"
                :default-managed-roles="managerManagedRoleValues"
                :default-manager-permissions="localConfig.approverManagement"
                @input="emitUpdate"
            />
        </section>

        <section class="workflow-section">
            <div class="workflow-section__header">
                <div>
                    <h6 class="workflow-section__title">
                        <i class="fas fa-clock"></i>
                        Типовые сроки этапов
                    </h6>
                    <div class="workflow-section__subtitle">
                        Базовые часы для согласования, выполнения и приемки.
                    </div>
                </div>
            </div>

            <b-row>
                <b-col cols="12" md="4">
                    <b-form-group label="Согласование, часов" label-for="deadline-approval">
                        <b-form-input
                            id="deadline-approval"
                            v-model.number="localConfig.defaultDeadlines.approval"
                            type="number"
                            min="1"
                            @input="emitUpdate"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="Выполнение, часов" label-for="deadline-execution">
                        <b-form-input
                            id="deadline-execution"
                            v-model.number="localConfig.defaultDeadlines.execution"
                            type="number"
                            min="1"
                            @input="emitUpdate"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="Приемка, часов" label-for="deadline-acceptance">
                        <b-form-input
                            id="deadline-acceptance"
                            v-model.number="localConfig.defaultDeadlines.acceptance"
                            type="number"
                            min="1"
                            @input="emitUpdate"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
        </section>
    </div>
</template>

<script>
import { getAllStatuses, isFinalStatus } from '@/utils/requestStatuses';
import { DEFAULT_CONFIGURABLE_COMMENT_ACTIONS } from '@/utils/requestClosingComment';
import MultipleParticipantsConfig from '../MultipleParticipantsConfig/MultipleParticipantsConfig.vue';

export default {
    name: "WorkflowConfig",
    components: {
        MultipleParticipantsConfig
    },
    props: {
        value: {
            type: Object,
            default: () => ({})
        },
        users: {
            type: Array,
            default: () => []
        },
        departments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            activeTab: 0,
            closingCommentActionOptions: [
                { value: 'complete', text: 'Отправка на полную приёмку или завершение без приёмки' },
                { value: 'partially_complete', text: 'Отправка на частичную приёмку' },
                { value: 'accept', text: 'Подтверждение полного выполнения' },
                { value: 'accept_partially', text: 'Принятие частично выполненной заявки' },
                { value: 'change_status', text: 'Прямая смена администратором на финальный статус' },
                { value: 'other_final', text: 'Другой настроенный ручной переход в финальный статус' }
            ],
            // Все статусы из утилиты
            allStatuses: getAllStatuses(),
            
            // Справочник всех действий
            allActions: [
                { key: 'submit', label: 'Отправить заявку', description: 'Обязательное действие: инициатор запускает настроенный процесс; если согласующих нет, заявка автоматически переходит дальше', group: 'approval' },
                { key: 'approve', label: 'Согласовать', description: 'Согласующий одобряет заявку', group: 'approval' },
                { key: 'reject', label: 'Отклонить', description: 'Согласующий отклоняет заявку', group: 'approval' },
                { key: 'return', label: 'Вернуть на доработку', description: 'Вернуть заявку инициатору для исправления', group: 'approval' },
                { key: 'assign', label: 'Назначить исполнителя', description: 'Назначить конкретного исполнителя на заявку', group: 'execution' },
                { key: 'execute', label: 'Взять в работу', description: 'Исполнитель начинает работу над заявкой', group: 'execution' },
                { key: 'complete', label: 'Завершить выполнение', description: 'Исполнитель завершает работу', group: 'execution' },
                { key: 'partially_complete', label: 'Частично выполнить', description: 'Исполнитель завершает часть работы', group: 'execution' },
                { key: 'accept', label: 'Подтвердить выполнение', description: 'Инициатор подтверждает выполнение', group: 'acceptance' },
                { key: 'accept_partially', label: 'Принять частично', description: 'Инициатор принимает частичное выполнение', group: 'acceptance' },
                { key: 'cancel', label: 'Отменить', description: 'Отменить заявку', group: 'other' },
                { key: 'comment', label: 'Комментировать', description: 'Добавить комментарий к заявке', group: 'other' }
            ],

            // Справочник ролей
            allRoles: [
                { key: 'initiator', label: 'Инициатор', description: 'Создает заявку' },
                { key: 'approver', label: 'Согласующий', description: 'Согласовывает заявку' },
                { key: 'assigner', label: 'Назначающий', description: 'Назначает исполнителей' },
                { key: 'executor', label: 'Исполнитель', description: 'Выполняет заявку' },
                { key: 'observer', label: 'Наблюдатель', description: 'Просматривает' }                
            ],

            // Локальная конфигурация
            localConfig: {}
        }
    },
    computed: {
        // Действия по группам
        approvalActions() {
            return this.allActions.filter(a => a.group === 'approval');
        },
        executionActions() {
            return this.allActions.filter(a => a.group === 'execution');
        },
        acceptanceActions() {
            return this.allActions.filter(a => a.group === 'acceptance');
        },
        otherActions() {
            return this.allActions.filter(a => a.group === 'other');
        },
        actionGroups() {
            return [
                {
                    key: 'approval',
                    label: 'Согласование',
                    icon: 'fas fa-check-circle',
                    actions: this.approvalActions
                },
                {
                    key: 'execution',
                    label: 'Выполнение',
                    icon: 'fas fa-cogs',
                    actions: this.executionActions
                },
                {
                    key: 'acceptance',
                    label: 'Приемка',
                    icon: 'fas fa-clipboard-check',
                    actions: this.acceptanceActions
                },
                {
                    key: 'other',
                    label: 'Прочие действия',
                    icon: 'fas fa-ellipsis-h',
                    actions: this.otherActions
                }
            ];
        },
        managerManagedRoleOptions() {
            return this.allRoles.map(role => ({
                value: role.key,
                text: role.label
            }));
        },
        managerManagedRoleValues() {
            return this.managerManagedRoleOptions.map(role => role.value);
        },

        // Включенные действия (выбранные пользователем)
        enabledActions() {
            return this.allActions.filter(action => 
                this.localConfig.allowedActions && this.localConfig.allowedActions.includes(action.key)
            );
        },
        transitionCount() {
            const transitionsMap = this.localConfig.stateMachineConfig?.transitionsMap || {};
            return Object.values(transitionsMap).reduce((sum, transitions) => {
                return sum + (Array.isArray(transitions) ? transitions.length : 0);
            }, 0);
        },
        autoTransitionCount() {
            const autoTransitions = this.localConfig.autoTransitions || {};
            return Object.keys(autoTransitions).filter(key => Boolean(autoTransitions[key])).length;
        },
        workflowProblems() {
            return [
                ...this.transitionWarnings,
                ...this.permissionErrors,
                ...this.actionResultErrors
            ];
        },

        // Предупреждения о переходах
        transitionWarnings() {
            const warnings = [];
            
            // Проверка на недостижимые статусы
            const reachableStatuses = new Set(['draft']); // Черновик всегда доступен
            let changed = true;
            
            while (changed) {
                changed = false;
                for (const source of reachableStatuses) {
                    const transitions = this.localConfig.stateMachineConfig?.transitionsMap?.[source] || [];
                    for (const target of transitions) {
                        if (!reachableStatuses.has(target)) {
                            reachableStatuses.add(target);
                            changed = true;
                        }
                    }
                }
            }
            
            const unreachableStatuses = this.allStatuses
                .map(s => s.key)
                .filter(key => !reachableStatuses.has(key));
            
            if (unreachableStatuses.length > 0) {
                const labels = unreachableStatuses
                    .map(key => this.allStatuses.find(s => s.key === key)?.label)
                    .filter(Boolean)
                    .join(', ');
                warnings.push(`Недостижимые статусы: ${labels}`);
            }
            
            return warnings;
        },

        // Ошибки прав доступа
        permissionErrors() {
            const errors = [];
            
            for (const action of this.enabledActions) {
                const roles = this.localConfig.stateMachineConfig?.actionPermissions?.[action.key] || [];
                if (roles.length === 0) {
                    errors.push(`Действие "${action.label}" не назначено ни одной роли`);
                }
            }
            
            return errors;
        },

        // Ошибки результатов действий
        actionResultErrors() {
            const errors = [];
            
            for (const action of this.enabledActions) {
                const targetStatus = this.localConfig.stateMachineConfig?.actionToStatus?.[action.key];
                if (!targetStatus) {
                    errors.push(`Для действия "${action.label}" не указан целевой статус`);
                }
            }
            
            return errors;
        }
    },
    created() {
        this.localConfig = this.initializeConfig();
        // Синхронизируем с родителем сразу, чтобы при создании типа без правок вкладки
        // в payload уходил дефолтный конфиг (то, что отображается в UI)
        this.$nextTick(() => this.emitUpdate());
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.localConfig = this.mergeWithDefaults(newVal);
                }
            },
            deep: true
        }
    },
    methods: {
        // Получение статической дефолтной конфигурации
        getDefaultConfig() {
            return {
                allowedActions: ['submit', 'approve', 'reject', 'return', 'assign', 'execute', 'complete', 'accept', 'cancel'],
                requiredRoles: ['initiator', 'approver', 'executor'],
                optionalRoles: ['observer'],
                sequentialApproval: true,
                requiresAcceptance: true,
                closingComment: {
                    required: false,
                    requiredActions: [...DEFAULT_CONFIGURABLE_COMMENT_ACTIONS]
                },
                autoTransitions: {
                    submitted: null,
                    allApproved: 'awaiting_executor',
                    anyRejected: 'rejected'
                },
                defaultDeadlines: {
                    approval: 24,
                    execution: 168,
                    acceptance: 24
                },
                approverManagement: {
                    managers: {
                        participants: [],
                        allow_any_department_employee: false
                    },
                    canReplaceSameDepartment: false,
                    canReplaceAnyEmployee: false,
                    canAddAnyEmployee: false,
                    canRemove: false
                },
                requestEditing: {
                    allowAfterApproval: false,
                    allowAfterExecutionStartWithoutApprovers: false
                },
                multiExecutor: {
                    assignerEnabled: true,
                    selfAssignEnabled: true
                },
                listItemsPreview: {
                    enabled: false
                },
                deadlinesConfig: {
                    staleRequestReminderEnabled: false,
                    staleCheckHours: 24,
                    autoCloseAwaitingAcceptanceEnabled: false,
                    autoCloseAwaitingAcceptanceDays: 3
                },
                stateMachineConfig: {
                    transitionsMap: {
                        draft: ['submitted', 'cancelled'],
                        submitted: ['in_review', 'rejected', 'cancelled'],
                        in_review: ['in_review', 'approved', 'awaiting_executor', 'rejected', 'returned', 'cancelled'],
                        returned: ['submitted', 'cancelled'],
                        approved: ['awaiting_executor', 'in_progress'],
                        awaiting_executor: ['in_progress'],
                        in_progress: ['awaiting_acceptance', 'partially_completed', 'in_progress'],
                        partially_completed: ['awaiting_acceptance', 'partially_accepted'],
                        awaiting_acceptance: ['completed', 'returned', 'in_progress', 'partially_accepted'],
                        partially_accepted: ['completed', 'in_progress', 'partially_completed', 'awaiting_acceptance'],
                        completed: [],
                        rejected: [],
                        cancelled: []
                    },
                    actionPermissions: {
                        submit: ['initiator'],
                        approve: ['approver'],
                        reject: ['approver'],
                        return: ['approver', 'initiator'],
                        assign: ['executor', 'assigner'],
                        execute: ['executor'],
                        complete: ['executor'],
                        partially_complete: ['executor'],
                        accept: ['initiator'],
                        accept_partially: ['initiator'],
                        cancel: ['initiator'],
                        comment: ['initiator', 'approver', 'executor', 'assigner', 'observer']
                    },
                    actionToStatus: {
                        submit: 'submitted',
                        approve: 'in_review',
                        reject: 'rejected',
                        return: 'returned',
                        assign: 'in_progress',
                        execute: 'in_progress',
                        complete: 'awaiting_acceptance',
                        partially_complete: 'partially_completed',
                        accept: 'completed',
                        accept_partially: 'completed',
                        cancel: 'cancelled'
                    },
                    customLogic: {},
                    actionConstraints: {
                        edit_materials_list: {
                            allowedStatuses: ['draft', 'submitted', 'returned'],
                            errorMessage: 'Редактирование материалов доступно только в статусах Черновик, На согласовании и Возвращена на доработку'
                        },
                        comment: {
                            requiresParticipation: true,
                            errorMessage: 'Недостаточно прав для комментария'
                        }
                    }
                }
            };
        },

        // Инициализация конфигурации по умолчанию
        initializeConfig() {
            if (!this.value || Object.keys(this.value).length === 0) {
                return JSON.parse(JSON.stringify(this.getDefaultConfig()));
            }
            return this.mergeWithDefaults(this.value);
        },

        // Слияние с дефолтными значениями
        mergeWithDefaults(config) {
            const defaults = this.getDefaultConfig();
            const sourceConfig = config || {};
            const incomingDeadlines = sourceConfig.deadlinesConfig || {};
            const deadlinesConfig = {
                ...defaults.deadlinesConfig,
                ...incomingDeadlines
            };
            const approverManagementSource = {
                ...(sourceConfig.approver_management || {}),
                ...(sourceConfig.approverManagement || {}),
                ...(sourceConfig.participant_management || {}),
                ...(sourceConfig.participantManagement || {})
            };
            const managerSource = approverManagementSource.managers ||
                approverManagementSource.manager_config ||
                approverManagementSource.managerParticipants ||
                {};
            const approverManagement = {
                ...defaults.approverManagement,
                ...approverManagementSource,
                managers: {
                    ...defaults.approverManagement.managers,
                    ...(managerSource && typeof managerSource === 'object' ? managerSource : {})
                }
            };
            if (approverManagement.canReplaceSameDepartment !== true &&
                approverManagement.can_replace_same_department === true) {
                approverManagement.canReplaceSameDepartment = true;
            }
            if (approverManagement.canReplaceAnyEmployee !== true &&
                approverManagement.can_replace_any_employee === true) {
                approverManagement.canReplaceAnyEmployee = true;
            }
            if (approverManagement.canAddAnyEmployee !== true &&
                approverManagement.can_add_any_employee === true) {
                approverManagement.canAddAnyEmployee = true;
            }
            if (approverManagement.canRemove !== true &&
                approverManagement.can_remove === true) {
                approverManagement.canRemove = true;
            }
            const multiExecutorSource = {
                ...(sourceConfig.multi_executor || {}),
                ...(sourceConfig.multiExecutor || {})
            };
            const multiExecutor = {
                ...defaults.multiExecutor,
                assignerEnabled: multiExecutorSource.assignerEnabled !== false &&
                    multiExecutorSource.assigner_enabled !== false,
                selfAssignEnabled: multiExecutorSource.selfAssignEnabled !== false &&
                    multiExecutorSource.self_assign_enabled !== false
            };
            const listItemsPreviewSource = {
                ...(sourceConfig.list_items_preview || {}),
                ...(sourceConfig.listItemsPreview || {})
            };
            const listItemsPreview = {
                enabled: listItemsPreviewSource.enabled === true ||
                    sourceConfig.showItemsInList === true ||
                    sourceConfig.show_items_in_list === true
            };
            const requestEditingSource = {
                ...(sourceConfig.request_editing || {}),
                ...(sourceConfig.requestEditing || {})
            };
            const requestEditing = {
                allowAfterApproval: requestEditingSource.allowAfterApproval === true ||
                    requestEditingSource.allow_after_approval === true,
                allowAfterExecutionStartWithoutApprovers:
                    requestEditingSource.allowAfterExecutionStartWithoutApprovers === true ||
                    requestEditingSource.allow_after_execution_start_without_approvers === true ||
                    requestEditingSource.allowAfterWorkStartWithoutApprovers === true ||
                    requestEditingSource.allow_after_work_start_without_approvers === true
            };
            const closingCommentSource = {
                ...(sourceConfig.closing_comment || {}),
                ...(sourceConfig.closingComment || {})
            };
            const closingComment = {
                required: closingCommentSource.required === true ||
                    closingCommentSource.isRequired === true ||
                    closingCommentSource.is_required === true ||
                    sourceConfig.closingCommentRequired === true ||
                    sourceConfig.closing_comment_required === true,
                requiredActions: Array.isArray(closingCommentSource.requiredActions || closingCommentSource.required_actions)
                    ? (closingCommentSource.requiredActions || closingCommentSource.required_actions)
                        .map(action => String(action || '').trim())
                        .filter(action => DEFAULT_CONFIGURABLE_COMMENT_ACTIONS.indexOf(action) !== -1)
                    : [...DEFAULT_CONFIGURABLE_COMMENT_ACTIONS]
            };
            if (
                incomingDeadlines.staleRequestReminderEnabled === undefined &&
                Number(incomingDeadlines.staleCheckHours) > 0
            ) {
                deadlinesConfig.staleRequestReminderEnabled = true;
            }
            
            const allowedActionsSource = Array.isArray(sourceConfig.allowedActions)
                ? sourceConfig.allowedActions
                : defaults.allowedActions;
            const allowedActions = [...new Set(['submit', ...allowedActionsSource])];
            const transitionsMap = {
                ...defaults.stateMachineConfig.transitionsMap,
                ...(sourceConfig.stateMachineConfig?.transitionsMap || {})
            };
            const actionPermissions = {
                ...defaults.stateMachineConfig.actionPermissions,
                ...(sourceConfig.stateMachineConfig?.actionPermissions || {})
            };
            const actionToStatus = {
                ...defaults.stateMachineConfig.actionToStatus,
                ...(sourceConfig.stateMachineConfig?.actionToStatus || {})
            };
            actionToStatus.submit = actionToStatus.submit || 'submitted';
            transitionsMap.draft = Array.from(new Set([
                ...(Array.isArray(transitionsMap.draft) ? transitionsMap.draft : []),
                actionToStatus.submit
            ]));
            actionPermissions.submit = Array.from(new Set([
                ...(Array.isArray(actionPermissions.submit) ? actionPermissions.submit : []),
                'initiator'
            ]));

            return {
                ...sourceConfig,
                allowedActions,
                requiredRoles: sourceConfig.requiredRoles || defaults.requiredRoles,
                optionalRoles: sourceConfig.optionalRoles || defaults.optionalRoles,
                sequentialApproval: sourceConfig.sequentialApproval !== undefined ? sourceConfig.sequentialApproval : defaults.sequentialApproval,
                requiresAcceptance: sourceConfig.requiresAcceptance !== undefined ? sourceConfig.requiresAcceptance : defaults.requiresAcceptance,
                closingComment,
                autoTransitions: {
                    ...defaults.autoTransitions,
                    ...(sourceConfig.autoTransitions || {})
                },
                defaultDeadlines: {
                    ...defaults.defaultDeadlines,
                    ...(sourceConfig.defaultDeadlines || {})
                },
                approverManagement,
                requestEditing,
                multiExecutor,
                listItemsPreview,
                deadlinesConfig,
                stateMachineConfig: {
                    ...(sourceConfig.stateMachineConfig || {}),
                    transitionsMap,
                    actionPermissions,
                    actionToStatus,
                    customLogic: sourceConfig.stateMachineConfig?.customLogic || {},
                    actionConstraints: {
                        ...defaults.stateMachineConfig.actionConstraints,
                        ...(sourceConfig.stateMachineConfig?.actionConstraints || {})
                    }
                }
            };
        },

        // Проверка, разрешен ли переход
        isTransitionAllowed(sourceStatus, targetStatus) {
            const transitions = this.localConfig.stateMachineConfig?.transitionsMap?.[sourceStatus] || [];
            return transitions.includes(targetStatus);
        },
        isActionEnabled(actionKey) {
            return Boolean(this.localConfig.allowedActions && this.localConfig.allowedActions.includes(actionKey));
        },
        transitionTargetOptions() {
            // Самопереходы используются для продолжения процесса без смены общего
            // статуса заявки (например, in_progress -> in_progress при перехвате
            // исполнения). Текущий статус должен оставаться среди options, иначе
            // vue-select не может сопоставить сохраненный key со справочником,
            // показывает сырой "in_progress" и некорректно обновляет список.
            return this.allStatuses;
        },

        // Переключение перехода
        toggleTransition(sourceStatus, targetStatus, isChecked) {
            if (!this.localConfig.stateMachineConfig.transitionsMap[sourceStatus]) {
                this.$set(this.localConfig.stateMachineConfig.transitionsMap, sourceStatus, []);
            }
            
            const transitions = this.localConfig.stateMachineConfig.transitionsMap[sourceStatus];
            const index = transitions.indexOf(targetStatus);
            
            if (isChecked && index === -1) {
                transitions.push(targetStatus);
            } else if (!isChecked && index !== -1) {
                transitions.splice(index, 1);
            }
            
            this.emitUpdate();
        },

        // Проверка, финальный ли статус
        isFinalStatus(status) {
            return isFinalStatus(status);
        },

        // Проверка, есть ли право
        hasPermission(action, role) {
            const permissions = this.localConfig.stateMachineConfig?.actionPermissions?.[action] || [];
            return permissions.includes(role);
        },

        // Переключение права
        togglePermission(action, role, isChecked) {
            if (!this.localConfig.stateMachineConfig.actionPermissions[action]) {
                this.$set(this.localConfig.stateMachineConfig.actionPermissions, action, []);
            }
            
            const permissions = this.localConfig.stateMachineConfig.actionPermissions[action];
            const index = permissions.indexOf(role);
            
            if (isChecked && index === -1) {
                permissions.push(role);
            } else if (!isChecked && index !== -1) {
                permissions.splice(index, 1);
            }
            
            this.emitUpdate();
        },

        // Отправка обновления наверх
        emitUpdate() {
            this.$emit('input', JSON.parse(JSON.stringify(this.localConfig)));
        }
    }
}
</script>

<style scoped>
.workflow-config {
    color: #253242;
}

.workflow-overview {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.workflow-overview__item {
    padding: 0.75rem;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    background: #fbfcfe;
}

.workflow-overview__label {
    display: block;
    margin-bottom: 0.25rem;
    color: #6c757d;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
}

.workflow-section {
    padding: 1rem 0;
    border-top: 1px solid #e5e9ef;
}

.workflow-section:first-of-type {
    border-top: 0;
}

.workflow-section__header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.85rem;
}

.workflow-section__title {
    margin-bottom: 0.15rem;
    color: #24364b;
    font-weight: 700;
}

.workflow-section__subtitle {
    color: #6c757d;
    font-size: 0.88rem;
}

.workflow-process-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.workflow-process-options .custom-control {
    padding: 0.75rem 0.75rem 0.75rem 2.25rem;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    background: #fbfcfe;
}

.workflow-action-group + .workflow-action-group {
    margin-top: 1rem;
}

.workflow-action-group__title {
    margin-bottom: 0.45rem;
    color: #4b5d73;
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
}

.workflow-action-row {
    display: grid;
    grid-template-columns: minmax(220px, 0.7fr) minmax(360px, 1.3fr);
    gap: 1rem;
    align-items: start;
    padding: 0.85rem;
    border: 1px solid #e0e6ed;
    border-radius: 6px;
    background: #ffffff;
}

.workflow-action-row + .workflow-action-row {
    margin-top: 0.5rem;
}

.workflow-action-row--disabled {
    background: #f8f9fb;
}

.workflow-action-row__details {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(220px, 0.9fr);
    gap: 0.75rem;
    align-items: start;
}

.workflow-field-label {
    margin-bottom: 0.35rem;
    color: #4b5d73;
    font-size: 0.85rem;
    font-weight: 700;
}

.workflow-role-picker {
    min-width: 0;
}

.workflow-role-chip {
    display: inline-flex;
    margin-right: 0.75rem;
    margin-bottom: 0.35rem;
}

.workflow-result-select {
    margin-bottom: 0;
}

.workflow-transition-row {
    display: grid;
    grid-template-columns: minmax(190px, 0.45fr) minmax(0, 1fr);
    gap: 1rem;
    align-items: center;
    padding: 0.75rem 0;
    border-top: 1px solid #eef1f4;
}

.workflow-transition-row:first-of-type {
    border-top: 0;
}

.workflow-transition-row__source {
    min-width: 0;
}

.workflow-transition-row__targets {
    min-width: 0;
}

.workflow-permission-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem 1rem;
}

/* Стили для vue-select */
.workflow-config >>> .v-select {
    min-height: 38px;
}

.workflow-config >>> .v-select .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
}

.workflow-config >>> .v-select .vs__dropdown-toggle:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.workflow-config >>> .v-select .vs__search {
    margin: 0;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
}

.workflow-config >>> .v-select .vs__selected-options {
    padding: 0.375rem 0.75rem;
}

/* Стиль badges в селектах */
.workflow-config >>> .v-select .badge {
    font-size: 0.875rem;
}

@media (max-width: 992px) {
    .workflow-overview,
    .workflow-process-options,
    .workflow-permission-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workflow-action-row,
    .workflow-action-row__details,
    .workflow-transition-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 576px) {
    .workflow-process-options,
    .workflow-permission-grid {
        grid-template-columns: 1fr;
    }

    .workflow-overview {
        display: flex;
        gap: 0.55rem;
        overflow-x: auto;
        margin-left: -0.25rem;
        margin-right: -0.25rem;
        padding: 0 0.25rem 0.2rem;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    .workflow-overview::-webkit-scrollbar {
        display: none;
    }

    .workflow-overview__item {
        flex: 0 0 132px;
        padding: 0.55rem 0.65rem;
    }

    .workflow-overview__label {
        font-size: 0.68rem;
    }

    .workflow-section {
        padding: 0.8rem 0;
    }

    .workflow-section__header {
        margin-bottom: 0.65rem;
    }

    .workflow-section__title {
        font-size: 0.95rem;
    }

    .workflow-section__subtitle {
        font-size: 0.78rem;
    }

    .workflow-process-options .custom-control {
        padding: 0.65rem 0.65rem 0.65rem 2.2rem;
    }

    .workflow-action-group__title {
        font-size: 0.74rem;
    }

    .workflow-action-row {
        padding: 0.7rem;
        gap: 0.65rem;
    }

    .workflow-action-row__details {
        gap: 0.55rem;
    }

    .workflow-role-chip {
        margin-right: 0.45rem;
        margin-bottom: 0.25rem;
        font-size: 0.85rem;
    }

    .workflow-transition-row {
        gap: 0.5rem;
        padding: 0.65rem 0;
    }

    .workflow-config >>> .v-select .vs__search {
        font-size: 0.92rem;
    }
}
</style>

